import React, { useEffect, useState } from 'react';
import Modal from 'react-modal';
import InputMask from 'react-input-mask';
import { toast } from 'react-toastify';
import { editConsultantBankAccountById, editConsultantById, getBanks, getConsultantById } from '../../utils/api';
import IntermediaryPasswordModal from './IntermediaryPasswordModal';
import './IntermediaryProfileModal.css';

Modal.setAppElement('#root');

const initialForm = {
    name: '',
    phonenumber: '',
    email: '',
    status: '',
    consultant_bank_account_id: '',
    bank_account: {
        sameownership: '',
        name: '',
        doc_type: '',
        doc_number: '',
        bank_id: '',
        account_type: '',
        agency_number: '',
        account_number: '',
        pix_key: '',
        pix_number: '',
    },
};

const statusLabels = {
    A: 'Ativo',
    I: 'Inativo',
};

const normalizeBank = (bank) => ({
    id: bank.id || bank.bank_id || bank.code || bank.codigo || '',
    name: bank.name || bank.bank_name || bank.nome || bank.label || '',
});

const accountTypeOptions = ['Corrente', 'Poupanca', 'Pagamento'];

const pixKeyOptions = [
    { value: 'CPF', label: 'CPF' },
    { value: 'CNPJ', label: 'CNPJ' },
    { value: 'Telefone', label: 'Telefone' },
    { value: 'Email', label: 'Email' },
    { value: 'Chave aleatoria', label: 'Chave aleatoria' },
];

const getPixMask = (pixKey) => {
    const normalizedPixKey = String(pixKey || '').toLowerCase();

    if (normalizedPixKey.includes('cpf')) {
        return '999.999.999-99';
    }

    if (normalizedPixKey.includes('cnpj')) {
        return '99.999.999/9999-99';
    }

    if (normalizedPixKey.includes('telefone') || normalizedPixKey.includes('celular') || normalizedPixKey.includes('phone')) {
        return '(99) 99999-9999';
    }

    return '';
};

export default function IntermediaryProfileModal({ isOpen, consultantId, onClose, onProfileUpdated }) {
    const [form, setForm] = useState(initialForm);
    const [banks, setBanks] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [passwordModalOpen, setPasswordModalOpen] = useState(false);

    useEffect(() => {
        if (!isOpen) {
            setPasswordModalOpen(false);
        }
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen || !consultantId) {
            return;
        }

        async function loadProfile() {
            setLoading(true);

            try {
                const consultant = await getConsultantById(consultantId);

                setForm({
                    name: consultant.name || '',
                    phonenumber: consultant.phonenumber || '',
                    email: consultant.email || '',
                    status: consultant.status || '',
                    consultant_bank_account_id: consultant.consultant_bank_account_id || '',
                    bank_account: {
                        sameownership: consultant.bank_account?.sameownership || '',
                        name: consultant.bank_account?.name || '',
                        doc_type: consultant.bank_account?.doc_type || '',
                        doc_number: consultant.bank_account?.doc_number || '',
                        bank_id: consultant.bank_account?.bank_id || '',
                        account_type: consultant.bank_account?.account_type || '',
                        agency_number: consultant.bank_account?.agency_number || '',
                        account_number: consultant.bank_account?.account_number || '',
                        pix_key: consultant.bank_account?.pix_key || '',
                        pix_number: consultant.bank_account?.pix_number || '',
                    },
                });
            } catch (error) {
                toast.error('Nao foi possivel carregar os dados do perfil.');
            } finally {
                setLoading(false);
            }
        }

        loadProfile();
    }, [consultantId, isOpen]);

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        async function loadBanks() {
            try {
                const response = await getBanks();
                const bankList = Array.isArray(response) ? response : response?.data || [];
                const normalizedBanks = bankList
                    .map(normalizeBank)
                    .filter((bank) => bank.id && bank.name);

                setBanks(normalizedBanks);
            } catch (error) {
                setBanks([]);
            }
        }

        loadBanks();
    }, [isOpen]);

    const handleChange = (event) => {
        const { name, value } = event.target;

        setForm((currentForm) => ({
            ...currentForm,
            [name]: value,
        }));
    };

    const handleBankChange = (event) => {
        const { name, value } = event.target;

        setForm((currentForm) => ({
            ...currentForm,
            bank_account: {
                ...currentForm.bank_account,
                [name]: value,
                ...(name === 'pix_key' ? { pix_number: '' } : {}),
            },
        }));
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        if (!form.name.trim() || !form.phonenumber.trim() || !form.email.trim()) {
            toast.info('Preencha nome, telefone e email.');
            return;
        }

        setSaving(true);
        const toastId = toast.loading('Salvando perfil...');

        try {
            const consultantPayload = {
                name: form.name,
                phonenumber: form.phonenumber,
                email: form.email,
            };
            const bankPayload = {
                sameownership: form.bank_account.sameownership,
                name: form.bank_account.name,
                doc_type: form.bank_account.doc_type,
                doc_number: form.bank_account.doc_number,
                bank_id: form.bank_account.bank_id ? parseInt(form.bank_account.bank_id, 10) : null,
                account_type: form.bank_account.account_type,
                agency_number: form.bank_account.agency_number,
                account_number: form.bank_account.account_number,
                pix_key: form.bank_account.pix_key,
                pix_number: form.bank_account.pix_number,
            };

            await editConsultantById(consultantId, consultantPayload);

            if (form.consultant_bank_account_id) {
                await editConsultantBankAccountById(form.consultant_bank_account_id, bankPayload);
            }

            toast.update(toastId, {
                render: 'Perfil atualizado com sucesso!',
                type: 'success',
                isLoading: false,
                autoClose: 2500,
            });
            onProfileUpdated?.({
                name: form.name,
                phonenumber: form.phonenumber,
                email: form.email,
            });
            onClose();
        } catch (error) {
            toast.update(toastId, {
                render: 'Nao foi possivel salvar o perfil.',
                type: 'error',
                isLoading: false,
                autoClose: 3000,
            });
        } finally {
            setSaving(false);
        }
    };

    return (
        <>
        <Modal
            isOpen={isOpen}
            onRequestClose={onClose}
            contentLabel="Perfil do intermediario"
            className="intermediary-profile-modal"
            overlayClassName="intermediary-profile-overlay"
            shouldCloseOnOverlayClick
        >
            <div className="intermediary-profile-header">
                <div>
                    <span>Perfil</span>
                    <h2>Dados do intermediario</h2>
                    <p>Status: {statusLabels[form.status] || form.status || '-'}</p>
                </div>
                <div className="intermediary-profile-header-actions">
                    <button type="button" className="intermediary-profile-password-button" onClick={() => setPasswordModalOpen(true)}>
                        Alterar senha
                    </button>
                    <button type="button" className="intermediary-profile-close" onClick={onClose} aria-label="Fechar perfil">X</button>
                </div>
            </div>

            {loading ? (
                <div className="intermediary-profile-loading">Carregando dados...</div>
            ) : (
                <form className="intermediary-profile-form" onSubmit={handleSubmit}>
                    <label className="intermediary-profile-field intermediary-profile-field-wide">
                        <span>Nome</span>
                        <input
                            type="text"
                            name="name"
                            value={form.name}
                            onChange={handleChange}
                        />
                    </label>

                    <label className="intermediary-profile-field">
                        <span>Telefone</span>
                        <input
                            type="text"
                            name="phonenumber"
                            value={form.phonenumber}
                            onChange={handleChange}
                        />
                    </label>

                    <label className="intermediary-profile-field">
                        <span>Email</span>
                        <input
                            type="email"
                            name="email"
                            value={form.email}
                            onChange={handleChange}
                        />
                    </label>

                    <div className="intermediary-profile-section-title">
                        <h3>Dados bancarios</h3>
                    </div>

                    <label className="intermediary-profile-field intermediary-profile-field-compact">
                        <span>Mesma titularidade</span>
                        <select
                            name="sameownership"
                            value={form.bank_account.sameownership}
                            onChange={handleBankChange}
                        >
                            <option value="">Selecione</option>
                            <option value="S">Sim</option>
                            <option value="N">Nao</option>
                        </select>
                    </label>

                    <label className="intermediary-profile-field intermediary-profile-field-large">
                        <span>Nome do titular</span>
                        <input
                            type="text"
                            name="name"
                            value={form.bank_account.name}
                            onChange={handleBankChange}
                        />
                    </label>

                    <label className="intermediary-profile-field intermediary-profile-field-compact">
                        <span>Tipo de documento</span>
                        <select
                            name="doc_type"
                            value={form.bank_account.doc_type}
                            onChange={handleBankChange}
                        >
                            <option value="">Selecione</option>
                            <option value="CPF">CPF</option>
                            <option value="CNPJ">CNPJ</option>
                        </select>
                    </label>

                    <label className="intermediary-profile-field intermediary-profile-field-large">
                        <span>Documento</span>
                        <input
                            type="text"
                            name="doc_number"
                            value={form.bank_account.doc_number}
                            onChange={handleBankChange}
                        />
                    </label>

                    <label className="intermediary-profile-field intermediary-profile-field-wide">
                        <span>Banco</span>
                        <select
                            name="bank_id"
                            value={form.bank_account.bank_id}
                            onChange={handleBankChange}
                        >
                            <option value="">Selecione</option>
                            {banks.map((bank) => (
                                <option key={bank.id} value={bank.id}>{bank.name}</option>
                            ))}
                        </select>
                    </label>

                    <label className="intermediary-profile-field intermediary-profile-field-third">
                        <span>Tipo de conta</span>
                        <select
                            name="account_type"
                            value={form.bank_account.account_type}
                            onChange={handleBankChange}
                        >
                            <option value="">Selecione</option>
                            {accountTypeOptions.map((accountType) => (
                                <option key={accountType} value={accountType}>{accountType}</option>
                            ))}
                        </select>
                    </label>

                    <label className="intermediary-profile-field intermediary-profile-field-third">
                        <span>Agencia</span>
                        <input
                            type="text"
                            name="agency_number"
                            value={form.bank_account.agency_number}
                            onChange={handleBankChange}
                        />
                    </label>

                    <label className="intermediary-profile-field intermediary-profile-field-third">
                        <span>Conta</span>
                        <input
                            type="text"
                            name="account_number"
                            value={form.bank_account.account_number}
                            onChange={handleBankChange}
                        />
                    </label>

                    <label className="intermediary-profile-field intermediary-profile-field-half">
                        <span>Tipo de chave Pix</span>
                        <select
                            name="pix_key"
                            value={form.bank_account.pix_key}
                            onChange={handleBankChange}
                        >
                            <option value="">Selecione</option>
                            {pixKeyOptions.map((pixKey) => (
                                <option key={pixKey.value} value={pixKey.value}>{pixKey.label}</option>
                            ))}
                        </select>
                    </label>

                    <label className="intermediary-profile-field intermediary-profile-field-half">
                        <span>Chave Pix</span>
                        {getPixMask(form.bank_account.pix_key) ? (
                            <InputMask
                                mask={getPixMask(form.bank_account.pix_key)}
                                maskChar={null}
                                type="text"
                                name="pix_number"
                                value={form.bank_account.pix_number}
                                onChange={handleBankChange}
                            />
                        ) : (
                            <input
                                type={form.bank_account.pix_key === 'Email' ? 'email' : 'text'}
                                name="pix_number"
                                value={form.bank_account.pix_number}
                                onChange={handleBankChange}
                            />
                        )}
                    </label>

                    <div className="intermediary-profile-footer">
                        <button type="button" className="intermediary-profile-cancel" onClick={onClose}>
                            Cancelar
                        </button>
                        <button type="submit" className="intermediary-profile-save" disabled={saving}>
                            {saving ? 'Salvando...' : 'Salvar'}
                        </button>
                    </div>
                </form>
            )}
        </Modal>
        <IntermediaryPasswordModal
            isOpen={passwordModalOpen}
            consultantId={consultantId}
            onClose={() => setPasswordModalOpen(false)}
        />
        </>
    );
}
